import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Row, Col, Space, Typography, Spin, message, Modal, Input, Table, Tag } from 'antd';
import { ArrowLeft, Edit, Trash } from 'lucide-react';
import { fetchVendorById, updateVendor, deleteVendor, fetchPayables } from '../utils/api';
import { isViewer } from '../utils/auth';

const { Title } = Typography;

const totalFormatter = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });

const statusLabel = (status) => {
    if (status === 'paid') return 'Paid';
    if (status === 'partial') return 'Partially Paid';
    return 'Pending';
};

export default function VendorDetails({ currentUser = null }) {
    const { id } = useParams();
    const navigate = useNavigate();
    const [vendor, setVendor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [payables, setPayables] = useState([]);
    const [payablesLoading, setPayablesLoading] = useState(true);

    // Edit modal state
    const [showEditModal, setShowEditModal] = useState(false);
    const [editForm, setEditForm] = useState({});

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const data = await fetchVendorById(id);
                setVendor(data);
            } catch (e) {
                message.error('Failed to load vendor details');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [id]);

    useEffect(() => {
        const loadPayables = async () => {
            setPayablesLoading(true);
            try {
                const data = await fetchPayables({ vendor: id });
                setPayables(data.payables || []);
            } catch (e) {
                console.error('Failed to load vendor payables:', e);
            } finally {
                setPayablesLoading(false);
            }
        };
        if (id) loadPayables();
    }, [id]);

    const handleDelete = async () => {
        Modal.confirm({
            title: 'Delete Vendor',
            content: 'Are you sure you want to delete this vendor? This action cannot be undone.',
            okText: 'Delete',
            okType: 'danger',
            onOk: async () => {
                try {
                    await deleteVendor(id);
                    message.success('Vendor deleted');
                    navigate('/vendors');
                } catch (e) {
                    message.error('Failed to delete vendor');
                }
            }
        });
    };

    const openEditModal = () => {
        setEditForm({
            name: vendor.name || '',
            email: vendor.email || '',
            phone: vendor.phone || '',
            gstin: vendor.gstin || '',
            address: vendor.address || ''
        });
        setShowEditModal(true);
    };

    const handleEditSubmit = async () => {
        try {
            if (!editForm.name.trim()) {
                message.error('Name is required');
                return;
            }
            const updated = await updateVendor(id, editForm);
            setVendor(updated);
            setShowEditModal(false);
            message.success('Vendor updated');
        } catch (e) {
            message.error('Failed to update vendor');
        }
    };

    const payableColumns = [
        {
            title: 'Invoice #',
            dataIndex: 'invoiceNumber',
            key: 'invoiceNumber',
            render: (text, record) => (
                <a onClick={() => navigate(`/payable/${record._id}`)} style={{ cursor: 'pointer' }}>
                    {text}
                </a>
            ),
        },
        {
            title: 'Invoice Date',
            dataIndex: 'invoiceDate',
            key: 'invoiceDate',
            render: (date) => date ? new Date(date).toLocaleDateString() : '-',
        },
        {
            title: 'Due Date',
            dataIndex: 'paymentDueDate',
            key: 'paymentDueDate',
            render: (date) => date ? new Date(date).toLocaleDateString() : '-',
        },
        {
            title: 'Amount',
            dataIndex: 'totalAmount',
            key: 'totalAmount',
            render: (amt) => totalFormatter.format(amt || 0),
        },
        {
            title: 'Status',
            dataIndex: 'paymentStatus',
            key: 'paymentStatus',
            render: (status) => (
                <Tag color={status === 'paid' ? 'success' : status === 'partial' ? 'processing' : 'warning'}>
                    {statusLabel(status)}
                </Tag>
            ),
        },
    ];

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!vendor) {
        return (
            <div className="page-container">
                <Button icon={<ArrowLeft size={16} />} onClick={() => navigate('/vendors')}>Back to Vendors</Button>
                <Card style={{ marginTop: 16 }}>
                    <p>Vendor not found</p>
                </Card>
            </div>
        );
    }

    return (
        <div className="page-container">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Button icon={<ArrowLeft size={16} />} onClick={() => navigate('/vendors')}>Back to Vendors</Button>
                <Space>
                    {!isViewer() && (
                        <Button icon={<Edit size={16} />} onClick={openEditModal}>Edit</Button>
                    )}
                    {currentUser?.role === 'admin' && (
                        <Button danger icon={<Trash size={16} />} onClick={handleDelete}>Delete</Button>
                    )}
                </Space>
            </div>

            <Card title="Vendor Details">
                <Row gutter={[24, 16]}>
                    <Col span={8}>
                        <Typography.Text type="secondary">Name</Typography.Text>
                        <div style={{ fontSize: 14 }}>{vendor.name || '-'}</div>
                    </Col>
                    <Col span={8}>
                        <Typography.Text type="secondary">GSTIN</Typography.Text>
                        <div style={{ fontSize: 14 }}>{vendor.gstin || '-'}</div>
                    </Col>
                    <Col span={8}>
                        <Typography.Text type="secondary">Email</Typography.Text>
                        <div style={{ fontSize: 14 }}>{vendor.email || '-'}</div>
                    </Col>
                    <Col span={8}>
                        <Typography.Text type="secondary">Phone</Typography.Text>
                        <div style={{ fontSize: 14 }}>{vendor.phone || '-'}</div>
                    </Col>
                    <Col span={16}>
                        <Typography.Text type="secondary">Address</Typography.Text>
                        <div style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>{vendor.address || '-'}</div>
                    </Col>
                </Row>
            </Card>

            {/* Vendor Invoices Section */}
            <Card title={`Invoices (${payables.length})`} style={{ marginTop: 16 }}>
                <Table
                    dataSource={payables}
                    rowKey="_id"
                    loading={payablesLoading}
                    pagination={false}
                    columns={payableColumns}
                    locale={{ emptyText: 'No invoices found for this vendor' }}
                />
            </Card>

            {/* Edit Modal */}
            <Modal
                title="Edit Vendor"
                open={showEditModal}
                onCancel={() => setShowEditModal(false)}
                onOk={handleEditSubmit}
                okText="Save"
                width={600}
            >
                <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <div style={{ marginBottom: 12 }}>
                                <label>Name</label>
                                <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
                            </div>
                        </Col>
                        <Col span={12}>
                            <div style={{ marginBottom: 12 }}>
                                <label>GSTIN</label>
                                <Input value={editForm.gstin} onChange={(e) => setEditForm({ ...editForm, gstin: e.target.value })} />
                            </div>
                        </Col>
                        <Col span={12}>
                            <div style={{ marginBottom: 12 }}>
                                <label>Email</label>
                                <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
                            </div>
                        </Col>
                        <Col span={12}>
                            <div style={{ marginBottom: 12 }}>
                                <label>Phone</label>
                                <Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
                            </div>
                        </Col>
                        <Col span={24}>
                            <div style={{ marginBottom: 12 }}>
                                <label>Address</label>
                                <Input.TextArea rows={3} value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} />
                            </div>
                        </Col>
                    </Row>
                </div>
            </Modal>
        </div>
    );
}

